import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

interface ChangesSummary {
  changeSummary: string;
  changesApplied: string[];
  originalLength: number;
  updatedLength: number;
}

interface DocumentAIChatProps {
  lawsuitId?: string;
  documentContent: string;
  onDocumentUpdate: (newContent: string, changesSummary: ChangesSummary) => void;
  onClose: () => void;
  language?: string;
}

const DocumentAIChat: React.FC<DocumentAIChatProps> = ({
  lawsuitId,
  documentContent,
  onDocumentUpdate,
  onClose,
  language = 'en'
}) => {
  const { t } = useTranslation();
  const [quickEditText, setQuickEditText] = useState('');
  const [lastChangesSummary, setLastChangesSummary] = useState<ChangesSummary | null>(null);

  // Document edit mutation
  const editDocumentMutation = useMutation({
    mutationFn: async (changeRequest: string) => {
      if (!lawsuitId) {
        throw new Error('Lawsuit ID is required for AI document editing');
      }
      const response = await axios.post('/api/ai/edit-document', {
        lawsuitId,
        documentContent,
        changeRequest,
        language
      });
      return response.data;
    },
    onSuccess: (data) => {
      const { updatedDocument, changeSummary, changesApplied, originalLength, updatedLength } = data.data;
      
      // Create changes summary object
      const changesSummary: ChangesSummary = {
        changeSummary,
        changesApplied,
        originalLength,
        updatedLength
      };
      
      // Update the document in the parent component
      onDocumentUpdate(updatedDocument, changesSummary);
      
      // Store changes summary
      setLastChangesSummary(changesSummary);

      // Clear quick edit
      setQuickEditText('');
    }
  });

  const handleQuickEdit = () => {
    if (!quickEditText.trim() || !lawsuitId) return;
    editDocumentMutation.mutate(quickEditText);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuickEdit();
    }
  };

  // Check if we have lawsuit context
  if (!lawsuitId) {
    return (
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-red-50">
          <div className="flex items-center space-x-2">
            <SparklesIcon className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {t('aiChat.quickEditTitle', 'AI Quick Edit')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Warning Message */}
        <div className="flex-1 p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <XMarkIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('aiChat.noLawsuitContext', 'AI Assistant requires lawsuit context to function properly.')}
            </h3>
            <p className="text-sm text-gray-500">
              {t('aiChat.addLawsuitContext', 'Please ensure this document is associated with a lawsuit to use AI editing features.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-blue-50">
        <div className="flex items-center space-x-2">
          <SparklesIcon className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t('aiChat.quickEditTitle', 'AI Quick Edit')}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Last changes summary */}
      {lastChangesSummary && (
        <div className="p-4 border-b border-gray-200 bg-green-50">
          <div className="flex items-start space-x-2">
            <CheckIcon className="h-5 w-5 text-green-600 mt-1" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-green-800 mb-1">
                {t('aiChat.lastChangesTitle', 'Last Changes Applied')}
              </h4>
              <p className="text-sm text-green-700 mb-2">
                {lastChangesSummary.changeSummary}
              </p>
              <div className="text-xs text-green-600">
                {t('aiChat.lengthChange', 'Length changed from {{original}} to {{updated}} characters', {
                  original: lastChangesSummary.originalLength,
                  updated: lastChangesSummary.updatedLength
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Edit Section */}
      <div className="flex-1 p-4">
        <div className="flex items-start space-x-2">
          <DocumentTextIcon className="h-5 w-5 text-blue-600 mt-1" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              {t('aiChat.quickEditDescription', 'Describe what you want to change in the document')}
            </h4>
            <textarea
              value={quickEditText}
              onChange={(e) => setQuickEditText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('aiChat.quickEditPlaceholder', 'Describe what you want to change in the document...')}
              className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={6}
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-500">
                {t('aiChat.quickEditHint', 'Press Enter to apply changes, Shift+Enter for new line')}
              </span>
              <button
                onClick={handleQuickEdit}
                disabled={!quickEditText.trim() || editDocumentMutation.isPending || !lawsuitId}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {editDocumentMutation.isPending && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>
                  {editDocumentMutation.isPending 
                    ? t('aiChat.applying', 'Applying...') 
                    : t('aiChat.applyChanges', 'Apply Changes')
                  }
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentAIChat;